import { createContext, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

const LangContext = createContext();

// La lingua dipende SOLO dall'URL: /en/... = inglese, tutto il resto = italiano.
// Ogni lingua ha URL distinti e pre-renderizzati: Google indicizza entrambe le versioni.
export const langFromPath = (pathname) =>
  pathname === "/en" || pathname.startsWith("/en/") ? "en" : "it";

// URL della stessa pagina in una data lingua (usato dai selettori IT/EN e dagli hreflang)
export const pathForLang = (pathname, lang) => {
  const en = langFromPath(pathname) === "en";
  if (lang === "en") return en ? pathname : pathname === "/" ? "/en/" : `/en${pathname}`;
  return en ? pathname.replace(/^\/en/, "") || "/" : pathname;
};

export const LangProvider = ({ children }) => {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  useEffect(() => {
    localStorage.setItem("pw-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={{ lang }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const STR = {
  it: {
    nav: {
      home: "Home", studio: "Studio", servizi: "Servizi", progetti: "Progetti",
      gallery: "Gallery", contatti: "Contatti", cta: "Richiedi un preventivo",
    },
    hero: {
      side: "Precisione  |  Competenza  |  Affidabilità",
      l1: "Progettiamo", l2: "soluzioni,", l3: "realizziamo", l4: "valore.",
      sub1: "Progettazione, rilievi e direzione lavori",
      sub2: "con competenza, innovazione e precisione.",
      cta: "Scopri i nostri servizi",
    },
    services: {
      eyebrow: "I nostri servizi",
      title: "Competenze al tuo servizio",
      intro: "Offriamo servizi tecnici integrati per accompagnare ogni progetto dall’idea alla realizzazione.",
      all: "Tutti i servizi",
      items: [
        { title: "Progettazione", desc: "Progetti su misura, funzionali ed efficienti, in ogni fase e scala." },
        { title: "Rilievi", desc: "Rilievi topografici e laser scanner ad alta precisione per ogni esigenza." },
        { title: "Direzione Lavori", desc: "Gestione e controllo del cantiere per garantire qualità, tempi e costi." },
        { title: "Consulenza Tecnica", desc: "Supporto tecnico e normativo per privati, aziende e pubbliche amministrazioni." },
      ],
      moreTitle: "E inoltre — servizi e consulenze",
      list: [
        "Consulenza tecnico-immobiliare e urbanistica",
        "Studio di lottizzazioni residenziali, produttive e commerciali",
        "Conformità urbanistiche e catastali, sanatorie edilizie",
        "Pratiche edilizie e catastali",
        "Rilievi geometrici e topografici",
        "Certificazioni energetiche",
        "Perizie ordinarie, giurate e asseverate",
        "Consulenza tecnica D.Lgs. 81/08",
        "Successioni, divisioni e frazionamenti",
        "Tabelle millesimali",
        "Consulenze tecniche di parte",
        "Digital Home Staging e marketing immobiliare",
      ],
    },
    portfolio: {
      eyebrow: "Progetti recenti",
      title: "Idee che prendono forma",
      intro: "Scopri alcuni dei progetti seguiti dal nostro studio.",
      servicesLine: "Progettazione • Direzione lavori",
      cats: { Residenziale: "Residenziale", Ristrutturazioni: "Ristrutturazioni", Commerciale: "Commerciale" },
    },
    gallery: {
      eyebrow: "Gallery",
      title: "I nostri lavori in immagini",
      intro: "Una selezione di scatti dai cantieri e dai progetti dello studio.",
    },
    about: {
      eyebrow: "Lo Studio",
      t1: "Competenza,", t2: "esperienza,", t3: "visione.",
      text: "Project Works nasce dalla collaborazione di professionisti con competenze complementari e una visione condivisa: offrire soluzioni tecniche di qualità, affidabili e innovative. Dopo singole esperienze, due soci condividono oggi idee, progetti e metodi in un unico gruppo di lavoro.",
      cta: "Scopri di più",
      imgAlt: "Francesco e Stefano in cantiere — Project Works",
    },
    contact: {
      eyebrow: "Contatti",
      titlePre: "Parliamo del tuo", titleAccent: "progetto.",
      intro: "Raccontaci cosa hai in mente. Ti aiuteremo a individuare il percorso tecnico più adatto.",
      cardTitle: "Studio Tecnico Associato",
      namePh: "Nome e Cognome",
      phonePh: "Telefono",
      subjectPh: "Oggetto",
      messagePh: "Raccontaci il tuo progetto",
      privacyPre: "Accetto la",
      privacyLink: "privacy policy",
      submit: "Invia richiesta",
      sending: "Invio in corso",
      success: "Richiesta inviata. Ti ricontatteremo al più presto.",
      error: "Invio non riuscito. Riprova o contattaci telefonicamente.",
    },
    footer: {
      quickLinks: "Link rapidi",
      services: "Servizi",
      contacts: "Contatti",
      rights: "Tutti i diritti riservati",
    },
    legal: { back: "Torna alla home", updated: "Ultimo aggiornamento" },
  },
  en: {
    nav: {
      home: "Home", studio: "Studio", servizi: "Services", progetti: "Projects",
      gallery: "Gallery", contatti: "Contact", cta: "Request a quote",
    },
    hero: {
      side: "Precision  |  Expertise  |  Reliability",
      l1: "We design", l2: "solutions,", l3: "we build", l4: "value.",
      sub1: "Design, surveys and construction management",
      sub2: "with expertise, innovation and precision.",
      cta: "Discover our services",
    },
    services: {
      eyebrow: "Our services",
      title: "Skills at your service",
      intro: "We offer integrated technical services to support every project from idea to completion.",
      all: "All services",
      items: [
        { title: "Design", desc: "Tailor-made, functional and efficient projects, at every stage and scale." },
        { title: "Surveys", desc: "High-precision topographic and laser scanner surveys for every need." },
        { title: "Construction Management", desc: "Site management and control to guarantee quality, time and costs." },
        { title: "Technical Consultancy", desc: "Technical and regulatory support for private clients, companies and public authorities." },
      ],
      moreTitle: "And more — services and consultancy",
      list: [
        "Technical, real estate and urban planning consultancy",
        "Residential, industrial and commercial development studies",
        "Urban planning and cadastral compliance, building amnesties",
        "Building and cadastral permits",
        "Geometric and topographic surveys",
        "Energy performance certificates",
        "Ordinary, sworn and certified appraisals",
        "Technical consultancy Legislative Decree 81/08",
        "Inheritance, division and land subdivision",
        "Condominium ownership tables",
        "Party-appointed technical consultancy",
        "Digital Home Staging and real estate marketing",
      ],
    },
    portfolio: {
      eyebrow: "Recent projects",
      title: "Ideas taking shape",
      intro: "Discover some of the projects followed by our studio.",
      servicesLine: "Design • Construction management",
      cats: { Residenziale: "Residential", Ristrutturazioni: "Renovations", Commerciale: "Commercial" },
    },
    gallery: {
      eyebrow: "Gallery",
      title: "Our work in pictures",
      intro: "A selection of shots from the studio's construction sites and projects.",
    },
    about: {
      eyebrow: "The Studio",
      t1: "Expertise,", t2: "experience,", t3: "vision.",
      text: "Project Works was born from the collaboration of professionals with complementary skills and a shared vision: delivering high-quality, reliable and innovative technical solutions. After individual careers, two partners now share ideas, projects and methods in a single working group.",
      cta: "Discover more",
      imgAlt: "Francesco and Stefano on site — Project Works",
    },
    contact: {
      eyebrow: "Contact",
      titlePre: "Let's talk about your", titleAccent: "project.",
      intro: "Tell us what you have in mind. We will help you find the most suitable technical path.",
      cardTitle: "Studio Tecnico Associato",
      namePh: "First and last name",
      phonePh: "Phone",
      subjectPh: "Subject",
      messagePh: "Tell us about your project",
      privacyPre: "I accept the",
      privacyLink: "privacy policy",
      submit: "Send request",
      sending: "Sending",
      success: "Request sent. We will get back to you as soon as possible.",
      error: "Sending failed. Please try again or contact us by phone.",
    },
    footer: {
      quickLinks: "Quick links",
      services: "Services",
      contacts: "Contacts",
      rights: "All rights reserved",
    },
    legal: { back: "Back to home", updated: "Last updated" },
  },
};
